import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { dragDebug } from "../admin/dragDebug";

/** Extract a stable string id from an item — supports both `id` and `_id` (MongoDB / DataRow). */
function getId(item: any): string {
  return (item.id ?? item._id ?? "") as string;
}

// ── Module-level active-drag singleton ──────────────────────────────────
// Only one drag can be active at a time.  Pointer events are delivered to
// window-level listeners (capture phase) so they work reliably inside
// sandboxed iframes where the browser's native HTML5 drag-and-drop `drop`
// event is often suppressed.

interface ActiveDrag {
  hookId: number;
  sourceId: string;
  sourceEl: HTMLElement;
  pointerId?: number;
  startX: number;
  startY: number;
  active: boolean;
}

let activeDrag: ActiveDrag | null = null;
let hookCounter = 0;

/** Nearest-centre hit-test: find the card whose centre is closest to (x,y). */
function findCardUnder(
  x: number,
  y: number,
  exclude: HTMLElement,
  groupId: number,
): HTMLElement | null {
  const all = Array.from(
    document.querySelectorAll(`[data-drag-group="${groupId}"]`),
  ) as HTMLElement[];
  let best: HTMLElement | null = null;
  let bestDist = Infinity;
  for (const c of all) {
    if (c === exclude) continue;
    const r = c.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const d = (x - cx) ** 2 + (y - cy) ** 2;
    if (d < bestDist) {
      bestDist = d;
      best = c;
    }
  }
  return best;
}

/**
 * Reusable hook for drag-and-drop reordering of items.
 * Uses **pointer events** (not HTML5 DnD) so drops work reliably inside
 * sandboxed iframes.  Persists the order to localStorage.
 *
 * @param storageKey  Unique localStorage key for this list
 * @param items       The raw array of items (each must have a unique `id` or `_id` field)
 * @returns orderedItems, drag state, and a `getDragProps(id)` helper
 */
export function useDragSort<T extends Record<string, any>>(
  storageKey: string,
  items: T[],
) {
  // Stable hook identity so window listeners know which instance is dragging.
  const hookIdRef = useRef(++hookCounter);
  const hookId = hookIdRef.current;

  // ── saved order (array of ids) ──
  const [idOrder, setIdOrder] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw) as string[];
    } catch {
      /* ignore corrupt data */
    }
    return [];
  });

  const persist = useCallback(
    (order: string[]) => {
      localStorage.setItem(storageKey, JSON.stringify(order));
    },
    [storageKey],
  );

  // ── derive ordered items ──
  const orderedItems = useMemo(() => {
    if (idOrder.length === 0) return items;
    const itemIds = items.map((i) => getId(i));
    const idSet = new Set(itemIds);

    // Start with saved order, keeping only ids that still exist
    const kept = idOrder.filter((id) => idSet.has(id));
    // Append any new items not yet in the saved order
    const newIds = itemIds.filter((id) => !kept.includes(id));
    const merged = [...kept, ...newIds];

    // Build a lookup for O(1) access
    const lookup = new Map(items.map((i) => [getId(i), i]));
    return merged.map((id) => lookup.get(id)!).filter(Boolean);
  }, [items, idOrder]);

  // ── refs so window listeners always see latest values ──
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const idOrderRef = useRef(idOrder);
  idOrderRef.current = idOrder;
  const persistRef = useRef(persist);
  persistRef.current = persist;

  // ── drag UI state (for visual feedback) ──
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  // ── window-level pointermove / pointerup ────────────────────────────
  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      if (!activeDrag || activeDrag.hookId !== hookId) return;

      const dx = e.clientX - activeDrag.startX;
      const dy = e.clientY - activeDrag.startY;

      // Require a 6px dead-zone before activating so clicks don't trigger drag.
      if (!activeDrag.active) {
        if (Math.hypot(dx, dy) < 6) return;
        activeDrag.active = true;
        // Capture the pointer only once a real drag starts (past the dead-zone).
        // Capturing on pointerdown retargets the browser's `click` event to the
        // wrapper, which silently breaks card onClick navigation.
        if (activeDrag.pointerId != null) {
          try {
            activeDrag.sourceEl.setPointerCapture(activeDrag.pointerId);
          } catch {
            /* ignore */
          }
        }
        document.body.style.userSelect = "none";
        document.body.style.cursor = "grabbing";
        activeDrag.sourceEl.style.opacity = "0.4";
        activeDrag.sourceEl.style.transition = "none";
      }

      const target = findCardUnder(
        e.clientX,
        e.clientY,
        activeDrag.sourceEl,
        hookId,
      );
      const targetId = target?.getAttribute("data-drag-id") || null;
      setDragOverId(
        targetId && targetId !== activeDrag.sourceId ? targetId : null,
      );
    };

    const cleanupDrag = () => {
      if (!activeDrag || activeDrag.hookId !== hookId) return;
      activeDrag.sourceEl.style.opacity = "";
      activeDrag.sourceEl.style.transition = "";
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!activeDrag || activeDrag.hookId !== hookId) return;

      const sourceId = activeDrag.sourceId;
      const sourceEl = activeDrag.sourceEl;
      const wasActive = activeDrag.active;

      // Find drop target (only if the drag actually moved past the dead-zone).
      const target = wasActive
        ? findCardUnder(e.clientX, e.clientY, sourceEl, hookId)
        : null;
      const targetId = target?.getAttribute("data-drag-id") || null;

      cleanupDrag();
      activeDrag = null;
      setDragId(null);
      setDragOverId(null);

      // Swap items
      if (!wasActive) {
        dragDebug("dash: drop ignored — no movement", "#9ca3af");
      } else if (!targetId || targetId === sourceId) {
        dragDebug("dash: no drop target", "#b45309");
      } else {
        const currentOrder =
          idOrderRef.current.length > 0
            ? [...idOrderRef.current]
            : itemsRef.current.map((i) => getId(i));
        const srcIdx = currentOrder.indexOf(sourceId);
        const tgtIdx = currentOrder.indexOf(targetId);
        if (srcIdx === -1 || tgtIdx === -1) {
          dragDebug(`dash: id mismatch (src ${srcIdx}, tgt ${tgtIdx})`, "#dc2626");
        } else {
          currentOrder.splice(srcIdx, 1);
          currentOrder.splice(tgtIdx, 0, sourceId);
          setIdOrder(currentOrder);
          try {
            persistRef.current(currentOrder);
            dragDebug(`✓ dash reordered`, "#16a34a");
          } catch (err: any) {
            dragDebug(`✗ dash persist FAILED\n${err?.message || err}`, "#dc2626");
          }
        }
      }
    };

    window.addEventListener("pointermove", onPointerMove, true);
    window.addEventListener("pointerup", onPointerUp, true);
    window.addEventListener("pointercancel", onPointerUp, true);

    return () => {
      window.removeEventListener("pointermove", onPointerMove, true);
      window.removeEventListener("pointerup", onPointerUp, true);
      window.removeEventListener("pointercancel", onPointerUp, true);
      // If this hook owned the active drag, tear it down.
      if (activeDrag && activeDrag.hookId === hookId) {
        cleanupDrag();
        activeDrag = null;
        setDragId(null);
        setDragOverId(null);
      }
    };
  }, [hookId]);

  // ── props factory for each draggable row / card ─────────────────────
  const getDragProps = useCallback(
    (id: string) => {
      const onPointerDown = (e: React.PointerEvent) => {
        if (e.button !== 0) return; // primary button only
        // Don't start drag from interactive children (buttons, links, inputs…)
        if (
          (e.target as HTMLElement).closest("a,button,input,textarea,select")
        )
          return;
        e.stopPropagation();

        const el = e.currentTarget as HTMLElement;

        activeDrag = {
          hookId,
          sourceId: id,
          sourceEl: el,
          pointerId: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          active: false,
        };

        setDragId(id);
      };

      return {
        "data-drag-id": id,
        "data-drag-group": hookId,
        onPointerDown,
        // Kill native image/link drag so it doesn't interfere.
        onDragStart: (e: React.DragEvent) => e.preventDefault(),
        style: { touchAction: "none" as const },
      };
    },
    [hookId],
  );

  return {
    orderedItems,
    dragId,
    dragOverId,
    getDragProps,
  };
}
